import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { PanelHeader } from "../../../../components/UIToolkit/molecules/PanelHeader";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, NEUTRAL, SHADOW, SURFACE } from "../../../../theme/tokens";

type Props = {
	/** Leading glyph for the header's `AccentIconTile`. */
	icon: ReactNode;
	title: string;
	subtitle: string;
	/** Header-right cluster — the Revert affordance, a legend, a settings popover. */
	headerActions?: ReactNode;
	/** How many cards exist in total (not how many are rendered) — drives the "show more" affordance. */
	totalCount: number;
	/** How many are currently rendered. Each rendered card owns one query, so this bounds the fetches. */
	visibleCount: number;
	onShowMore: () => void;
	emptyIcon: ReactNode;
	emptyTitle: string;
	emptyHint: string;
	children: ReactNode;
	dataTestId: string;
};

/**
 * The shell both opportunity tabs share: a titled section header with an action cluster, a two-column
 * card grid, its empty state, and a bounded "show more".
 *
 * The card count is bounded on purpose — each card fetches its own staff list, so "render everything"
 * would fire one request per recommendation (25 at the sampled department) the moment a tab opens.
 * Revealing in batches keeps the number of in-flight queries proportional to what the user is actually
 * looking at, which is also why `visibleCount` lives here rather than inside each card.
 */
const OpportunitySection: FC<Props> = ({
	icon,
	title,
	subtitle,
	headerActions,
	totalCount,
	visibleCount,
	onShowMore,
	emptyIcon,
	emptyTitle,
	emptyHint,
	children,
	dataTestId,
}) => {
	const { t } = useTranslation();
	const remaining = totalCount - visibleCount;

	return (
		<Box
			data-testid={dataTestId}
			sx={{ mb: 4, borderRadius: 2, backgroundColor: SURFACE, border: `1px solid ${NEUTRAL[200]}`, boxShadow: SHADOW.PANEL_SOFT, overflow: "hidden" }}
		>
			<PanelHeader
				icon={icon}
				title={title}
				subtitle={subtitle}
				endingContent={headerActions}
				sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${NEUTRAL[150]}` }}
			/>

			{totalCount === 0 ? (
				<EmptyState icon={emptyIcon} iconColor="success.dark" title={emptyTitle} hint={emptyHint} dataTestId={`${dataTestId}-empty`} />
			) : (
				<Box sx={{ px: 2.5, py: 2.5 }}>
					<Box
						// `start`, not the grid default `stretch`: a card whose staff list came back empty would
						// otherwise be stretched to its row-mate's height and read as a broken card.
						sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, alignItems: "start", gap: 2 }}
						data-testid={`${dataTestId}-grid`}
					>
						{children}
					</Box>
					{remaining > 0 && (
						<Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
							<SystemButton buttonVariant="secondary" size="compact" noFullWidthMobile onClick={onShowMore} data-testid={`${dataTestId}-show-more`}>
								{t("hiring.opportunity.showMore", { count: remaining })}
							</SystemButton>
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: NEUTRAL[500] }}>
								{t("hiring.opportunity.shownOf", { shown: visibleCount, total: totalCount })}
							</Typography>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};

export default OpportunitySection;
